/**
 * Shared consultant Online/Offline presence for all ProfileOnlineButton instances
 * (sidebar + dashboard) and socket connect logic.
 *
 * Rules:
 * - manualOfflineStatus=offline means the consultant CHOSE offline → stay offline
 *   until they tap Online (survives refresh).
 * - Auto-online preference applies only at LOGIN, not on every page refresh.
 */

export const PRESENCE_EVENT = "prontopsy-presence-change";
const MANUAL_KEY = "manualOfflineStatus";
const SIGNIN_AUTO_KEY = "prontopsy_signin_auto_online";

/** Login just requested Online. Control panel must not treat a stale server Offline as a manual tap. */
export function markSignInAutoOnline() {
  try {
    sessionStorage.setItem(SIGNIN_AUTO_KEY, String(Date.now()));
  } catch (_) {
    /* ignore */
  }
}

/** Both sidebar + dashboard Online widgets must see this. Do not delete on read. */
export function consumeSignInAutoOnline() {
  try {
    const raw = sessionStorage.getItem(SIGNIN_AUTO_KEY);
    if (!raw) return false;
    const at = Number(raw);
    return Number.isFinite(at) && Date.now() - at < 2 * 60 * 1000;
  } catch (_) {
    return false;
  }
}

export function clearSignInAutoOnline() {
  try {
    sessionStorage.removeItem(SIGNIN_AUTO_KEY);
  } catch (_) {
    /* ignore */
  }
}

export function isManualOffline() {
  try {
    return localStorage.getItem(MANUAL_KEY) === "offline";
  } catch (_) {
    return false;
  }
}

/** Login-only: honor the "Auto Online when I sign in" checkbox. */
export function shouldAutoOnlineOnSignIn() {
  try {
    const { getAutoOnlineOnSignIn } = require("./consultantNotes");
    return getAutoOnlineOnSignIn() !== false;
  } catch (_) {
    return true;
  }
}

export function setManualOffline(offline) {
  try {
    if (offline) localStorage.setItem(MANUAL_KEY, "offline");
    else localStorage.removeItem(MANUAL_KEY);
  } catch (_) {
    /* ignore */
  }
  broadcastPresence(offline ? "offline" : "online");
}

export function broadcastPresence(status) {
  try {
    window.dispatchEvent(
      new CustomEvent(PRESENCE_EVENT, { detail: { status: status || "offline" } }),
    );
  } catch (_) {
    /* ignore */
  }
}

/** Subscribe all UI instances to the same status */
export function subscribePresence(handler) {
  const onCustom = (e) => handler(e?.detail?.status);
  const onStorage = (e) => {
    if (e.key === MANUAL_KEY) {
      handler(e.newValue === "offline" ? "offline" : "online");
    }
  };
  window.addEventListener(PRESENCE_EVENT, onCustom);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(PRESENCE_EVENT, onCustom);
    window.removeEventListener("storage", onStorage);
  };
}

/**
 * Map a userStatusResponse payload to online | busy | offline.
 * A boolean `online: true` must not overwrite an in-call "busy" status —
 * heartbeats and socket reconnects broadcast that shape.
 */
export function resolveLiveStatus(event, currentStatus) {
  if (!event) return currentStatus || "offline";
  if (event.onlineStatus === "busy" || event.busy === true) return "busy";
  if (event.onlineStatus === "offline" || event.online === false) return "offline";
  if (event.onlineStatus === "online" || event.online === true) {
    return currentStatus === "busy" ? "busy" : "online";
  }
  return currentStatus || "offline";
}

/** Home / Find-a-psychologist sort: Online → Busy → Offline */
export function consultantStatusRank(status) {
  if (status === "online") return 0;
  if (status === "busy") return 1;
  return 2;
}

export function compareConsultantStatus(aStatus, bStatus) {
  return consultantStatusRank(aStatus) - consultantStatusRank(bStatus);
}

const TABS_KEY = "prontopsy_active_tabs";
const TAB_TTL_MS = 20000;

export function getTabId() {
  try {
    let tabId = sessionStorage.getItem("prontopsy_tab_id");
    if (!tabId) {
      tabId = "tab_" + Math.random().toString(36).substring(2, 9) + "_" + Date.now();
      sessionStorage.setItem("prontopsy_tab_id", tabId);
    }
    return tabId;
  } catch (_) {
    return "tab_default";
  }
}

export function registerTabSession() {
  try {
    const tabId = getTabId();
    const now = Date.now();
    const raw = localStorage.getItem(TABS_KEY);
    const tabs = raw ? JSON.parse(raw) : {};

    // Purge expired tabs
    Object.keys(tabs).forEach((id) => {
      if (now - tabs[id] > TAB_TTL_MS) {
        delete tabs[id];
      }
    });

    tabs[tabId] = now;
    localStorage.setItem(TABS_KEY, JSON.stringify(tabs));
  } catch (_) {
    /* ignore */
  }
}

export function unregisterTabSession() {
  try {
    const tabId = getTabId();
    const now = Date.now();
    const raw = localStorage.getItem(TABS_KEY);
    const tabs = raw ? JSON.parse(raw) : {};

    delete tabs[tabId];

    // Purge expired tabs
    Object.keys(tabs).forEach((id) => {
      if (now - tabs[id] > TAB_TTL_MS) {
        delete tabs[id];
      }
    });

    localStorage.setItem(TABS_KEY, JSON.stringify(tabs));
    return Object.keys(tabs).length;
  } catch (_) {
    return 0;
  }
}

export function getActiveTabCount() {
  try {
    const now = Date.now();
    const raw = localStorage.getItem(TABS_KEY);
    const tabs = raw ? JSON.parse(raw) : {};
    let count = 0;
    Object.keys(tabs).forEach((id) => {
      if (now - tabs[id] <= TAB_TTL_MS) {
        count++;
      }
    });
    return count;
  } catch (_) {
    return 1;
  }
}

/**
 * Cleanly teardown presence when the consultant closes their browser/tab.
 * Emits userOffline and immediately disconnects socket so backend updates instantly.
 * Also sends keepalive fetch to update REST DB status.
 */
export function handleTeardownPresence(socket, userId) {
  try {
    // If during an active call, do not force offline (leave to call coordinator)
    if (localStorage.getItem("appointmentData")) return;

    const remainingTabs = unregisterTabSession();
    if (remainingTabs > 0) {
      // Another tab is still open in this browser session
      return;
    }

    const liveSocket =
      socket ||
      (typeof window !== "undefined" && window.__prontoLiveSocket);

    const targetId =
      userId ||
      (() => {
        try {
          return JSON.parse(localStorage.getItem("userDetails") || "{}")?._id;
        } catch {
          return null;
        }
      })();

    if (liveSocket && liveSocket.connected) {
      if (targetId) {
        liveSocket.emit("userOffline", targetId);
      } else {
        liveSocket.emit("userOffline");
      }
      try {
        liveSocket.disconnect();
      } catch (_) {}
    }

    const token =
      typeof localStorage !== "undefined"
        ? localStorage.getItem("auth_token")
        : null;
    if (token && typeof fetch !== "undefined") {
      try {
        const BASE_URL =
          process.env.REACT_APP_API_URL ||
          "https://dev.api.prontopsy.com/api/v1";
        fetch(`${BASE_URL}/user/edit`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ onlineStatus: "offline" }),
          keepalive: true,
        }).catch(() => {});
      } catch (_) {}
    }
  } catch (_) {
    /* ignore */
  }
}

/**
 * Determine a consultant's presence status given the live status list and any raw DB fallback.
 * Live presence from consultantStatusList (/user/online-status + socket events) is authoritative.
 * If consultantStatusList is available, any consultant not found in the list is OFFLINE.
 */
export function getConsultantLivePresence(consultantId, consultantStatusList, fallbackDbStatus) {
  if (!consultantId) return "offline";

  if (consultantStatusList && Array.isArray(consultantStatusList)) {
    const found = consultantStatusList.find((item) => String(item?._id) === String(consultantId));
    if (found) {
      if (found.onlineStatus === "busy" || found.busy === true) return "busy";
      if (found.onlineStatus === "online" || found.online === true) return "online";
      return "offline";
    }
    // If live status list is loaded and consultant is NOT in it, they are offline!
    return "offline";
  }

  return fallbackDbStatus === "busy" ? "busy" : fallbackDbStatus === "online" ? "online" : "offline";
}

export function getConsultantReadiness(consultantId, consultantStatusList, fallbackDbAudio, fallbackDbVideo) {
  if (consultantStatusList && Array.isArray(consultantStatusList)) {
    const found = consultantStatusList.find((item) => String(item?._id) === String(consultantId));
    if (found) {
      return {
        readyForAudio: typeof found.readyForAudio === "boolean" ? found.readyForAudio : fallbackDbAudio === true,
        readyForVideo: typeof found.readyForVideo === "boolean" ? found.readyForVideo : fallbackDbVideo === true,
      };
    }
    return {
      readyForAudio: false,
      readyForVideo: false,
    };
  }
  return {
    readyForAudio: fallbackDbAudio === true,
    readyForVideo: fallbackDbVideo === true,
  };
}

